// ANGEL FLIGHT (color) — GlbEnemyView: a real rigged soldier per live enemy.
//
// Replaces the flat capsule blob with a cloned CC0 Kenney character (rigged +
// animated) — the single biggest realism win. Mirrors the ColorEnemyView public
// surface ColorWorld relies on (group, hitMeshes, setEnemyId, assign, flash,
// update, hide, dispose) so it is a drop-in.
//
// Each instance clones a preloaded template via SkeletonUtils (so the skinned
// mesh + skeleton clone correctly), drives its own AnimationMixer (walk/sprint →
// die on kill), and recolors its material clone per enemy skin. Hit zones come
// from three invisible box proxies (head/body/legs) parented to the rig, so the
// raycast keeps clean zones independent of the skinned geometry. If the template
// hasn't loaded yet, it falls back to a visible capsule so nothing is blank.

import * as THREE from 'three';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { BALANCE } from '../config/balance';
import { getEnemy } from '../config/enemies';
import { RENDER } from '../config/render';
import type { EnemyId } from '../core/types';
import { enemyAssetsReady, getTemplate, templateKeys } from './EnemyAssets';

// Invisible hit-proxy geometry (shared) — head/body/legs boxes for clean zones.
const HEAD_GEO = new THREE.BoxGeometry(0.34, 0.34, 0.34);
const BODY_GEO = new THREE.BoxGeometry(0.5, 0.7, 0.34);
const LEGS_GEO = new THREE.BoxGeometry(0.42, 0.7, 0.34);

// Procedural fallback FIGURE (only shown before the GLB template streams in, or
// if a load fails). The old fallback was a single capsule "pill" — the most
// block-like read in the game. This is a cheap assembled humanoid (head + torso
// + two arms + two legs) sharing ONE material, so it still reads as a SOLDIER,
// not a blob, even on the load race. Geometries are module-level + shared (never
// per-instance), aligned to the head/body/legs hit-proxy heights above.
const FB_HEAD_GEO = new THREE.SphereGeometry(0.17, 12, 9);
const FB_TORSO_GEO = new THREE.CapsuleGeometry(0.21, 0.42, 4, 10);
const FB_SHOULDER_GEO = new THREE.SphereGeometry(0.12, 8, 6);
const FB_ARM_GEO = new THREE.CapsuleGeometry(0.075, 0.44, 3, 6);
const FB_LEG_GEO = new THREE.CapsuleGeometry(0.095, 0.52, 3, 6);

/** Assemble the shared-geometry humanoid fallback under one material. Heights
 *  match the head(1.5)/body(1.05)/legs(0.45) hit proxies so it reads aligned. */
function buildFallbackFigure(mat: THREE.Material): THREE.Group {
  const g = new THREE.Group();
  const head = new THREE.Mesh(FB_HEAD_GEO, mat);
  head.position.set(0, 1.52, 0.02);
  const torso = new THREE.Mesh(FB_TORSO_GEO, mat);
  torso.position.set(0, 1.05, 0);
  const shoulderL = new THREE.Mesh(FB_SHOULDER_GEO, mat);
  shoulderL.position.set(-0.24, 1.24, 0);
  const shoulderR = new THREE.Mesh(FB_SHOULDER_GEO, mat);
  shoulderR.position.set(0.24, 1.24, 0);
  const armL = new THREE.Mesh(FB_ARM_GEO, mat);
  armL.position.set(-0.27, 0.98, 0.04);
  armL.rotation.z = 0.2;
  const armR = new THREE.Mesh(FB_ARM_GEO, mat);
  armR.position.set(0.27, 0.98, 0.04);
  armR.rotation.z = -0.2;
  const legL = new THREE.Mesh(FB_LEG_GEO, mat);
  legL.position.set(-0.1, 0.43, 0);
  const legR = new THREE.Mesh(FB_LEG_GEO, mat);
  legR.position.set(0.1, 0.43, 0);
  g.add(head, torso, shoulderL, shoulderR, armL, armR, legL, legR);
  return g;
}

/** Tint applied to the cloned character's colormap material. Friendlies (BLUEFOR)
 *  get a clear cool blue so the player can tell friend from foe at a glance. */
function tintFor(skin: string, friendly: boolean): number {
  if (friendly) return 0x6fa8ff; // BLUEFOR — distinct cool blue
  if (skin === 'brute') return 0x6a5a4a; // heavy, ashen tan
  if (skin === 'runner') return 0x7a8a5a; // sickly pale green
  return 0x55613a; // muddy olive infantry
}

const FLASH = new THREE.Color(0xfff2c0);

let rrCounter = 0; // round-robin template pick for variety
let gaitSeed = 0; // golden-angle phase spread so the horde never steps in unison

// Shared IFF diamond texture (built once on first friendly).
let _iffTex: THREE.Texture | null = null;
function iffTexture(): THREE.Texture {
  if (_iffTex) return _iffTex;
  const s = 64;
  const c = document.createElement('canvas');
  c.width = c.height = s;
  const g = c.getContext('2d');
  if (g) {
    g.strokeStyle = '#cfe8ff';
    g.lineWidth = 6;
    g.beginPath();
    g.moveTo(s / 2, 8);
    g.lineTo(s - 8, s / 2);
    g.lineTo(s / 2, s - 8);
    g.lineTo(8, s / 2);
    g.closePath();
    g.stroke();
  }
  _iffTex = new THREE.CanvasTexture(c);
  _iffTex.colorSpace = THREE.SRGBColorSpace;
  return _iffTex;
}

export class GlbEnemyView {
  readonly group: THREE.Group;
  readonly hitMeshes: THREE.Mesh[] = [];
  /** Sim-depth (m) at which the spawn-haze reveal completes — set by ColorWorld
   *  from the difficulty's revealZM (recruit clears deep, ace holds the ghost
   *  close). Null falls back to RENDER.spawnHaze.fadeClearZ. */
  revealClearZ: number | null = null;
  private readonly headProxy: THREE.Mesh;
  private readonly bodyProxy: THREE.Mesh;
  private readonly legsProxy: THREE.Mesh;

  private model: THREE.Object3D | null = null;
  private mixer: THREE.AnimationMixer | null = null;
  private clips: THREE.AnimationClip[] = [];
  private current: THREE.AnimationAction | null = null;
  private readonly skinMats: THREE.MeshStandardMaterial[] = [];
  private readonly baseColors: THREE.Color[] = [];
  private fallback: THREE.Object3D | null = null;

  private flashLeftMs = 0;
  private dying = false;
  /** Procedural topple used when no `die` clip exists (capsule fallback or a
   *  template without that animation) — otherwise the rig would freeze for the
   *  death beat and pop out. */
  private proceduralDie = false;
  private deathT = 0;
  /** Previous frame's sim depth — measures the unit's REAL closing rate so
   *  the gait playback can match motion (RENDER.gait). NaN = first frame. */
  private prevWorldZ = Number.NaN;
  /** Stride-cycle phase driving the procedural sway (RENDER.gait.sway). */
  private gaitPhase = 0;
  /** True when this unit's locomotion clip is `sprint` (picked by leg speed at
   *  assign) — the gait rate divides by the matching per-clip reference speed. */
  private sprinting = false;
  // Directional flinch: a quick recoil offset/lean that damps back to rest.
  private flinchX = 0;
  private flinchVel = 0;
  // BLUEFOR IFF marker (a floating blue diamond) — shown only for friendlies.
  private iffMarker: THREE.Sprite | null = null;

  constructor() {
    this.group = new THREE.Group();

    // Invisible hit proxies (raycastable, not rendered).
    const proxyMat = new THREE.MeshBasicMaterial({ visible: false });
    this.headProxy = new THREE.Mesh(HEAD_GEO, proxyMat);
    this.bodyProxy = new THREE.Mesh(BODY_GEO, proxyMat);
    this.legsProxy = new THREE.Mesh(LEGS_GEO, proxyMat);
    this.headProxy.position.set(0, 1.55, 0);
    this.bodyProxy.position.set(0, 1.05, 0);
    this.legsProxy.position.set(0, 0.4, 0);
    this.headProxy.userData.zone = 'head';
    this.bodyProxy.userData.zone = 'body';
    this.legsProxy.userData.zone = 'legs';
    this.group.add(this.headProxy, this.bodyProxy, this.legsProxy);
    this.hitMeshes.push(this.headProxy, this.bodyProxy, this.legsProxy);
  }

  setEnemyId(id: number): void {
    for (const m of this.hitMeshes) m.userData.enemyId = id;
  }

  assign(def: EnemyId): void {
    const e = getEnemy(def);
    const friendly = e.faction === 'civilian';
    this.flashLeftMs = 0;
    this.dying = false;
    this.proceduralDie = false;
    this.deathT = 0;
    this.prevWorldZ = Number.NaN; // pool reuse: forget the old unit's motion
    this.gaitPhase = (++gaitSeed * 2.399963) % (Math.PI * 2); // desync strides
    // Clip by LEG SPEED, not skin: only true sprinters (runner, 5.2 m/s) get
    // the sprint clip — 2.4-2.8 m/s walkers in a sprint clip played a floaty
    // slow-motion run. Also drives the procedural sway's reference rate.
    this.sprinting = e.speed >= RENDER.gait.sprintAtMps;
    this.group.rotation.x = 0; // pool reuse: undo any prior topple

    // (Re)build the visual model. Pool reuse: drop any prior model first.
    this.clearModel();

    if (enemyAssetsReady()) {
      const keys = templateKeys();
      const key = keys.length ? keys[(rrCounter++ % keys.length)] : undefined;
      const tpl = getTemplate(key ?? '');
      if (tpl) {
        const model = skeletonClone(tpl.scene) as THREE.Object3D;
        // Kenney characters are ~1 unit tall facing +Z; face the gunner (+Z in
        // Three) and scale to the enemy's world height.
        const tint = new THREE.Color(tintFor(e.skin, friendly));
        model.traverse((o) => {
          const m = o as THREE.Mesh;
          if (m.isMesh && m.material) {
            const src = m.material as THREE.MeshStandardMaterial;
            const mat = src.clone();
            mat.color.multiply(tint);
            mat.emissive = new THREE.Color(0x000000);
            mat.fog = true;
            // Spawn-haze reveal: rigs fade IN through the far fog bank
            // (opacity driven per-frame from worldZ in update()).
            mat.transparent = true;
            mat.opacity = 0;
            m.material = mat;
            this.skinMats.push(mat);
            this.baseColors.push(mat.color.clone());
          }
        });
        this.group.add(model);
        this.model = model;
        this.mixer = new THREE.AnimationMixer(model);
        this.clips = tpl.animations;
        this.play(this.sprinting ? 'sprint' : 'walk', true);
      }
    }

    if (!this.model) {
      // Humanoid fallback figure (template not ready yet) — a shaded soldier
      // silhouette, not a pill. One shared material drives the haze reveal +
      // hit flash exactly like the GLB skin mats.
      const mat = new THREE.MeshStandardMaterial({
        color: tintFor(e.skin, friendly),
        roughness: 0.9,
        metalness: 0.03,
        fog: true,
        transparent: true, // spawn-haze reveal (opacity set per-frame)
        opacity: 0,
      });
      this.fallback = buildFallbackFigure(mat);
      this.group.add(this.fallback);
      this.skinMats.push(mat);
      this.baseColors.push(mat.color.clone());
    }

    // BLUEFOR IFF marker: a glowing blue diamond floating above friendlies so the
    // player sees "ally — hold fire" at a glance. Hidden for hostiles.
    this.setIffMarker(friendly);

    // Scale the whole rig (proxies + model) to the enemy size. Kenney chars are
    // ~1.0 unit; scale so the rig is ~heightM tall.
    const s = e.heightM / 1.0;
    const wx = e.widthM / 0.7;
    this.group.scale.set(s * wx, s, s);
    this.group.visible = true;
  }

  /** Show/hide the floating BLUEFOR IFF diamond above the unit. */
  private setIffMarker(friendly: boolean): void {
    if (!friendly) {
      if (this.iffMarker) this.iffMarker.visible = false;
      return;
    }
    if (!this.iffMarker) {
      const mat = new THREE.SpriteMaterial({
        map: iffTexture(),
        color: 0x9fd0ff,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        fog: false,
      });
      this.iffMarker = new THREE.Sprite(mat);
      this.iffMarker.scale.setScalar(0.55);
      this.iffMarker.position.set(0, 2.2, 0);
      this.iffMarker.renderOrder = 999;
      this.group.add(this.iffMarker);
    }
    this.iffMarker.visible = true;
  }

  /** Cross-fade to the named clip. Returns false when the clip (or mixer) is
   *  missing so callers can fall back to a procedural read. */
  private play(name: string, loop: boolean): boolean {
    if (!this.mixer) return false;
    const clip = THREE.AnimationClip.findByName(this.clips, name);
    if (!clip) return false;
    const action = this.mixer.clipAction(clip);
    action.reset();
    action.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
    action.clampWhenFinished = !loop;
    if (this.current && this.current !== action) {
      this.current.fadeOut(0.15);
      action.fadeIn(0.15);
    }
    action.play();
    this.current = action;
    return true;
  }

  flash(ms: number): void {
    this.flashLeftMs = Math.max(this.flashLeftMs, ms);
  }

  /** Directional hit flinch: kick the rig back along the shot's lateral dir; a
   *  spring damps it back. Makes rounds visibly stagger the soldier. */
  flinch(dirX: number): void {
    this.flinchVel += dirX * 4.2;
  }

  /** Trigger the death animation (ColorWorld calls retire after a beat anyway,
   *  but if it tells us, we play `die`). */
  startDeath(): void {
    if (this.dying) return;
    this.dying = true;
    // No `die` clip (capsule fallback, or a template without one) → topple the
    // whole rig procedurally so the kill still reads as a collapse.
    if (!this.play('die', false)) {
      this.proceduralDie = true;
      this.current?.fadeOut(0.1); // stop walking mid-fall
    }
  }

  update(x: number, y: number, z: number, speedMps: number, dt: number, worldZ: number): void {
    this.group.position.set(x, y, z);
    this.group.rotation.y = Math.PI; // face down the band toward the gunner
    const dtSec = dt / 1000;
    const prox = Math.max(0, Math.min(1, 1 - worldZ / BALANCE.proj.farZ));

    // Spawn-haze reveal: invisible at/beyond the fog bank, fully clear once it
    // closes past fadeClearZ — the unit WALKS OUT of the murk, never pops in.
    // SMOOTHSTEPPED, not linear: linear opacity reads as a sudden appearance
    // (perceived visibility ramps hard at low alpha against the bright haze) —
    // the ease holds the unit as a faint ghost deep in the bank, then lets it
    // solidify as it closes.
    const H = RENDER.spawnHaze;
    const clearZ = this.revealClearZ ?? H.fadeClearZ;
    const r = Math.max(
      0,
      Math.min(1, (H.fadeHiddenZ - worldZ) / Math.max(0.001, H.fadeHiddenZ - clearZ)),
    );
    const reveal = r * r * (3 - 2 * r);
    for (const m of this.skinMats) m.opacity = reveal;
    if (this.iffMarker?.visible) this.iffMarker.material.opacity = reveal;
    // Gait matches MOTION (RENDER.gait): play the walk/sprint clip at the
    // unit's real ground-relative speed — the measured closing rate minus the
    // world drift the helicopter contributes. Fast movers visibly pump their
    // legs; a parked shooter idles to a shuffle instead of moonwalking; the
    // glide read ("they slide without walking") dies here. Dying rigs play at
    // 1× so the collapse isn't rushed. First frame falls back to def speed.
    const G = RENDER.gait;
    const measured = Number.isNaN(this.prevWorldZ)
      ? speedMps
      : Math.max(0, (this.prevWorldZ - worldZ) / Math.max(1e-6, dtSec) - RENDER.flight.scrollMps);
    this.prevWorldZ = worldZ;
    // Divide by the reference speed of the clip ACTUALLY playing — a sprint
    // clip at the walk reference rate looked like frantic leg-pumping.
    const refMps = this.sprinting ? G.sprintRefMps : G.walkRefMps;
    const rate = Math.max(G.rateMin, Math.min(G.rateMax, measured / refMps));
    this.mixer?.update(dtSec * (this.dying ? 1 : rate));

    // Procedural locomotion sway (RENDER.gait.sway): the overhead camera
    // foreshortens the authored leg swing to nothing, so the WALKING read from
    // above comes from this shoulder rock + stride bob + yaw swagger. Advanced
    // on the gait rate so the sway and the clip never desync; frozen while
    // dying so the collapse stays clean.
    const S = G.sway;
    if (!this.dying) {
      this.gaitPhase = (this.gaitPhase + dtSec * rate * S.strideHz * Math.PI * 2) % (Math.PI * 2);
    }
    const roll = this.dying ? 0 : Math.sin(this.gaitPhase) * S.rollRad;
    const yaw = this.dying ? 0 : Math.sin(this.gaitPhase) * S.yawRad;
    const bob = this.dying ? 0 : (0.5 - 0.5 * Math.cos(this.gaitPhase * 2)) * S.bobM;

    // Procedural collapse: topple the whole rig about the feet pivot, eased so
    // it accelerates into the ground like a real fall. (Position is left alone —
    // ColorWorld feeds the group's own position back in for dying rigs.)
    if (this.dying && this.proceduralDie) {
      this.deathT = Math.min(1, this.deathT + dtSec / 0.55);
      const k = 1 - (1 - this.deathT) ** 3; // ease-out cubic
      this.group.rotation.x = -k * Math.PI * 0.46;
    }

    // Flinch spring: lean + offset the visible model along the shot direction,
    // damping back to rest (stiffness/damping tuned for a snappy stagger).
    this.flinchVel += (-90 * this.flinchX - 13 * this.flinchVel) * dtSec;
    this.flinchX += this.flinchVel * dtSec;
    const lean = Math.max(-0.5, Math.min(0.5, this.flinchX));
    // Forward lunge lean as the unit bears down on the strike plane.
    const pitch = this.dying ? 0 : prox * 0.16;
    if (this.model) {
      this.model.rotation.z = lean * 0.6 + roll;
      this.model.rotation.x = pitch;
      this.model.rotation.y = yaw;
      this.model.position.x = lean * 0.12;
      this.model.position.y = bob;
    } else if (this.fallback) {
      // The figure is rooted at the feet (y=0); its parts carry their own
      // heights, so bob is just a small per-step bounce (not a +0.9 offset).
      this.fallback.rotation.z = lean * 0.6 + roll;
      this.fallback.rotation.x = pitch;
      this.fallback.rotation.y = yaw;
      this.fallback.position.y = bob;
    }

    if (this.flashLeftMs > 0) {
      this.flashLeftMs -= dt;
      for (const m of this.skinMats) {
        m.emissive.copy(FLASH);
        m.emissiveIntensity = 1.6;
      }
      if (this.flashLeftMs <= 0) this.restore();
    } else {
      for (const m of this.skinMats) {
        m.emissiveIntensity = prox * 0.25; // faint bear-down lift
        if (prox > 0) m.emissive.setRGB(0.5, 0.45, 0.3);
        else m.emissive.setRGB(0, 0, 0);
      }
    }
  }

  private restore(): void {
    for (let i = 0; i < this.skinMats.length; i++) {
      const m = this.skinMats[i];
      const c = this.baseColors[i];
      if (m && c) {
        m.color.copy(c);
        m.emissive.setRGB(0, 0, 0);
        m.emissiveIntensity = 1;
      }
    }
  }

  private clearModel(): void {
    if (this.model) {
      this.group.remove(this.model);
      this.model = null;
    }
    if (this.fallback) {
      this.group.remove(this.fallback);
      // The figure's part geometries are shared module-level consts — don't
      // dispose them (only the per-instance material, handled via skinMats).
      this.fallback = null;
    }
    this.mixer?.stopAllAction();
    this.mixer = null;
    this.current = null;
    for (const m of this.skinMats) m.dispose();
    this.skinMats.length = 0;
    this.baseColors.length = 0;
  }

  hide(): void {
    this.group.visible = false;
  }

  dispose(): void {
    this.clearModel();
  }
}
